<template>
  <template v-for="(run, index) in runs" :key="index">
    <a
      v-if="run.kind === 'link'"
      :href="run.href"
      target="_blank"
      rel="noopener noreferrer"
      class="font-[450] underline underline-offset-2 transition-opacity duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:opacity-65"
    >
      {{ run.value }}
    </a>

    <strong v-else-if="run.kind === 'bold'" class="font-[600]">
      {{ run.value }}
    </strong>

    <!-- eslint-disable tailwindcss/no-custom-classname -->
    <em v-else-if="run.kind === 'italic'" class="normalFontSynthesis">
      {{ run.value }}
    </em>

    <FootnoteMarker v-else-if="run.kind === 'note'" :marker="run.value" />

    <template v-else>{{ run.value }}</template>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import FootnoteMarker from '~/scopes/campaign/components/UI/FootnoteMarker.vue'
import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'

// Splits one authored string into inline runs so markdown-lite — `**bold**`,
// `[label](url)` and the `[^*]` footnote marker — renders as real
// <strong>/<a>/<sup> elements without fragmenting the sentence across translation
// keys. Renders inline with no block wrapper, so callers place it inside a <p>,
// <li>, etc. The template's tight tag spacing is deliberate: whitespace between
// runs would render as literal spaces inside words.
//
// The marker is asterisks only — `*` / `**` on metal sheets, after the client's sheets;
// articles carry none, citing through inline links. It draws through `FootnoteMarker`,
// which the metal view also paints directly on slots with no prose to hold a token.
//
// Note on comments: keep them in this block, never inside the `v-for` — a template
// comment there is re-emitted once per run, and story01 shipped 25 copies of one into
// its SSR payload.
const props = defineProps<{ message: I18nDescriptor }>()

const i18n = useI18n()

interface Run {
  kind: 'text' | 'bold' | 'link' | 'note' | 'italic'
  value: string
  href?: string
}

// `[^*]` leads the alternation so a marker is never mistaken for a link label — a link
// needs `(…)` right after the brackets, which is also what leaves bracketed editorial
// insertions (`[released in March 2026]`) untouched in a plain text run. The asterisks
// stay inside the brackets so a loose `**` can't collide with bold, and `\*+` is unbounded
// on purpose: a ceiling would have to agree with `footnoteMarker`'s, and a metal's fifth
// note would silently print its token as text.
const TOKEN = /(\[\^\*+\]|\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g
const NOTE = /^\[\^(\*+)\]$/
const LINK = /^\[([^\]]+)\]\(([^)]+)\)$/
const BOLD = /^\*\*([^*]+)\*\*$/
const ITALIC = /^_([^*]+)_$/

const runs = computed<Run[]>(() =>
  i18n(props.message)
    ?.split(TOKEN)
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      const note = segment.match(NOTE)
      if (note) return { kind: 'note', value: note[1] ?? '' }
      const link = segment.match(LINK)
      if (link)
        return {
          kind: 'link',
          value: link[1] ?? '',
          href: link[2] ?? '',
        }
      const bold = segment.match(BOLD)
      if (bold) return { kind: 'bold', value: bold[1] ?? '' }
      const italic = segment.match(ITALIC)
      if (italic) return { kind: 'italic', value: italic[1] ?? '' }
      return { kind: 'text', value: segment }
    }),
)
</script>

<style lang="css" scoped>
.normalFontSynthesis {
  font-synthesis: weight style small-caps;
}
</style>
